import  {
  createContext,
  useEffect,
  ReactNode,
  useContext,
  useMemo,
  useState,
} from "react";
import { ConfigProvider, theme as antdTheme } from "antd";

type ThemeMode = "light" | "dark";
type ThemeCtx = {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggle: () => void;
};

const ThemeContext = createContext<ThemeCtx | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const savedMode = localStorage.getItem("theme-mode");
    return savedMode === "dark" ? "dark" : "light";
  });

  const toggle = () => setMode((m) => (m === "light" ? "dark" : "light"));

  useEffect(() => {
    localStorage.setItem("theme-mode", mode);
    document.documentElement.dataset.theme = mode;
    document.documentElement.style.colorScheme = mode;
  }, [mode]);

  const antdConfig = useMemo(
    () => ({
      algorithm:
        mode === "dark" ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        
    }),
    [mode],
  );

  const ctxValue = useMemo(() => ({ mode, setMode, toggle }), [mode]);

  return (
    <ThemeContext.Provider value={ctxValue}>
      <ConfigProvider theme={antdConfig}>{children}</ConfigProvider>
    </ThemeContext.Provider>
  );
}

export function useThemeMode() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useThemeMode must be used within ThemeProvider");
  return ctx;
}
